import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import BatchToolsModal from "./BatchToolsModal";
import BatchImportModal from "./BatchImportModal";
import AiImageModal from "./AiImageModal";
import { getAdminListings, exportAdminListings, approveListing, rejectListing, adminDeleteListing, adminToggleListingStatus, AdminListingItem, batchDeleteListings, batchOfflineListings, batchUpdateListingTime, updateAdminListing } from "../../../utils/api";
import { getCategories } from "../../../utils/adminApi";

// 日期格式化辅助函数
function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const statusMap: Record<string, { label: string; className: string }> = {
  published: { label: "已发布", className: "bg-green-50 text-green-600" },
  pending: { label: "待审核", className: "bg-yellow-50 text-yellow-600" },
  offline: { label: "已下架", className: "bg-gray-100 text-gray-500" },
  reported: { label: "被举报", className: "bg-red-50 text-red-500" },
  active: { label: "已发布", className: "bg-green-50 text-green-600" },
};

const auditStatusMap: Record<string, { label: string; className: string }> = {
  approved: { label: "已通过", className: "bg-green-50 text-green-600" },
  pending: { label: "待审核", className: "bg-yellow-50 text-yellow-600" },
  rejected: { label: "已驳回", className: "bg-red-50 text-red-500" },
  active: { label: "已通过", className: "bg-green-50 text-green-600" },
};

// 导出列与「批量导入」模板一致，导出的 CSV 可直接再次用于批量导入
const LISTING_EXPORT_FIELDS: Array<{ key: string; label: string }> = [
  { key: "title", label: "标题" },
  { key: "category_name", label: "分类" },
  { key: "company_name", label: "公司名称" },
  { key: "contact_name", label: "联系人" },
  { key: "contact_phone", label: "电话" },
  { key: "contact_wechat", label: "微信" },
  { key: "content", label: "详情" },
  { key: "images", label: "图片" },
];

function csvEscape(value: unknown): string {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

function downloadListingsCsv(list: AdminListingItem[]): void {
  const header = LISTING_EXPORT_FIELDS.map((f) => f.label).join(",");
  const body = list.map((item: any) =>
    LISTING_EXPORT_FIELDS.map((f) => {
      let v: unknown = item[f.key];
      if (f.key === "images") {
        v = Array.isArray(item.images) ? item.images.join(",") : item.images || "";
      }
      return csvEscape(v);
    }).join(",")
  );
  const csv = "\uFEFF" + [header, ...body].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  a.download = `信息导出_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

interface RejectModalProps {
  listingTitle: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

function RejectModal({ listingTitle, onConfirm, onClose }: RejectModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError("请填写驳回原因");
      return;
    }
    onConfirm(reason.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-close-circle-line text-red-500 text-base" />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">驳回信息</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500 text-sm" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">驳回信息</p>
            <p className="text-sm text-gray-800 line-clamp-2">{listingTitle}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              驳回原因 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError(""); }}
              placeholder="请填写驳回原因，该内容将展示给发布者，帮助其修改后重新提交..."
              rows={4}
              maxLength={200}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none"
            />
            <div className="flex items-center justify-between mt-1">
              {error ? (
                <p className="text-xs text-red-500">{error}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400">{reason.length}/200</span>
            </div>
          </div>
          <div className="p-3 bg-yellow-50 rounded-lg">
            <p className="text-xs text-yellow-700">
              <i className="ri-information-line mr-1" />
              驳回后，发布者将在「我的发布」中看到驳回原因，可修改后重新提交审核。
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer">
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-2.5 bg-red-500 text-white text-sm font-medium rounded-xl hover:bg-red-600 transition-colors cursor-pointer"
          >
            确认驳回
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmModalProps {
  title: string;
  desc: string;
  confirmText: string;
  confirmClass: string;
  onConfirm: () => void;
  onClose: () => void;
}

function ConfirmModal({ title, desc, confirmText, confirmClass, onConfirm, onClose }: ConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-5 text-center space-y-3">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto">
            <i className="ri-error-warning-line text-red-500 text-xl" />
          </div>
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <p className="text-sm text-gray-500">{desc}</p>
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer">
            取消
          </button>
          <button onClick={onConfirm} className={`flex-1 py-2.5 text-white text-sm font-medium rounded-xl transition-colors cursor-pointer ${confirmClass}`}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminListingsPage() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<AdminListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [categoryList, setCategoryList] = useState<Array<{ id: string; name: string }>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showPageSizeMenu, setShowPageSizeMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<{ id: string; title: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<null | "batchDelete" | "batchOffline">(null);

  // 行内快速编辑状态 — 只记录哪个单元格在编辑，值由 EditableCell 内部独立管理
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editingSaveLoading, setEditingSaveLoading] = useState(false);

  // 加载分类数据
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getCategories();
        console.log('分类数据:', cats);
        // 扁平化分类树，只取 id 和 name
        const flatList: Array<{ id: string; name: string }> = [];
        const flatten = (items: any[]) => {
          for (const item of items) {
            flatList.push({ id: item.id, name: item.name });
            if (item.children && item.children.length > 0) {
              flatten(item.children);
            }
          }
        };
        flatten(cats);
        console.log('扁平化后:', flatList);
        setCategoryList(flatList);
      } catch (e) {
        console.error('加载分类失败:', e);
      }
    };
    loadCategories();
  }, []);

  // 加载数据
  const loadData = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params: any = {
        page: pageNum,
        page_size: pageSize,
        keyword: search,
      };
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterCategory !== "all") {
        params.category_id = filterCategory;
        console.log('筛选分类ID:', filterCategory);
      }
      if (filterDateStart) params.date_start = filterDateStart;
      if (filterDateEnd) params.date_end = filterDateEnd;
      
      console.log('请求参数:', params);
      const res = await getAdminListings(params);
      console.log('返回数据:', res);
      setListings(res.list);
      setTotal(res.total);
      setCurrentPage(pageNum);
      setSelectedIds([]);
    } catch (e) {
      console.error('加载失败:', e);
      showToast("加载数据失败");
    } finally {
      setLoading(false);
    }
  };

  // 监听 pageSize 变化
  useEffect(() => {
    setCurrentPage(1);
    loadData(1);
  }, [pageSize]);

  useEffect(() => {
    setCurrentPage(1);
    loadData(1);
  }, [filterStatus, search, filterCategory, filterDateStart, filterDateEnd]);

  // 计算过滤后的数据（仅用于前端搜索过滤）
  const filtered = listings.filter((l) => {
    const matchSearch =
      !search ||
      l.title.includes(search) ||
      (l.user_nickname && l.user_nickname.includes(search)) ||
      (l.category_name && l.category_name.includes(search));
    const matchStatus = filterStatus === "all" || l.status === filterStatus;
    const matchCategory = filterCategory === "all" || l.category_id === filterCategory;
    return matchSearch && matchStatus && matchCategory;
  });

  // 总页数基于服务器返回的 total
  const totalPages = Math.ceil(total / pageSize);
  const paginated = filtered;

  const allPageSelected =
    paginated.length > 0 && paginated.every((l) => selectedIds.includes(l.id));

  const hasDateFilter = filterDateStart || filterDateEnd;

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function toggleSelectAll() {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !paginated.find((l) => l.id === id)));
    } else {
      const newIds = paginated.map((l) => l.id).filter((id) => !selectedIds.includes(id));
      setSelectedIds((prev) => [...prev, ...newIds]);
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }

  function clearFilters() {
    setFilterCategory("all");
    setFilterDateStart("");
    setFilterDateEnd("");
    setShowDateFilter(false);
    setCurrentPage(1);
  }

  // 删除
  const handleDelete = async (id: string) => {
    try {
      await adminDeleteListing(id);
      setListings((prev) => prev.filter((l) => l.id !== id));
      setSelectedIds((prev) => prev.filter((i) => i !== id));
      showToast("删除成功");
    } catch (e) {
      showToast("删除失败");
    }
  };

  // 上下架
  const handleToggleStatus = async (id: string) => {
    try {
      await adminToggleListingStatus(id);
      setListings((prev) =>
        prev.map((l) =>
          l.id === id
            ? { ...l, status: l.status === "active" || l.status === "published" ? "offline" : "active" }
            : l
        )
      );
      showToast("状态更新成功");
    } catch (e) {
      showToast("操作失败");
    }
  };

  // 审核通过
  const handleApprove = async (id: string) => {
    try {
      await approveListing(id);
      setListings((prev) =>
        prev.map((l) =>
          l.id === id ? { ...l, status: "active", audit_status: "approved" as const } : l
        )
      );
      showToast("已通过审核，信息已发布");
    } catch (e) {
      showToast("操作失败");
    }
  };

  // 驳回
  const handleRejectConfirm = async (reason: string) => {
    if (!rejectTarget) return;
    try {
      await rejectListing(rejectTarget.id, reason);
      setListings((prev) =>
        prev.map((l) =>
          l.id === rejectTarget.id ? { ...l, status: "rejected", audit_status: "rejected" as const, reject_reason: reason } : l
        )
      );
      setRejectTarget(null);
      showToast("已驳回，驳回原因已通知发布者");
    } catch (e) {
      showToast("操作失败");
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    try {
      const result = await batchDeleteListings(selectedIds);
      // 从列表中移除已删除的项目
      setListings((prev) => prev.filter((l) => !selectedIds.includes(l.id)));
      setTotal((prev) => prev - result.count);
      setSelectedIds([]);
      setConfirmModal(null);
      showToast(`已删除 ${result.count} 条信息`);
    } catch (e) {
      showToast("批量删除失败");
    }
  };

  // 批量下架
  const handleBatchOffline = async () => {
    try {
      const result = await batchOfflineListings(selectedIds);
      // 更新列表中的状态
      setListings((prev) =>
        prev.map((l) =>
          selectedIds.includes(l.id) ? { ...l, status: "offline" } : l
        )
      );
      setSelectedIds([]);
      setConfirmModal(null);
      showToast(`已下架 ${result.count} 条信息`);
    } catch (e) {
      showToast("批量操作失败");
    }
  };

  // 批量替换发布时间
  const handleApplyTime = async (ids: string[], times: string[]) => {
    try {
      const result = await batchUpdateListingTime(ids, times);
      // 刷新列表以获取最新数据
      loadData(currentPage);
      showToast(`已成功更新 ${result.count} 条信息的发布时间`);
    } catch (e) {
      showToast("批量更新发布时间失败");
    }
  };

  function handleApplyPublisher(ids: string[], publishers: string[]) {
    // 发布者功能暂时只做前端模拟，不调用后端
    const count = ids.length > 0 ? ids.length : listings.length;
    showToast(`已成功为 ${count} 条信息随机分配发布者`);
  }

  // 批量导出：导出当前筛选条件下的全部数据（不限于当前页）
  const handleExport = async () => {
    setExporting(true);
    try {
      const params: any = { keyword: search };
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterCategory !== "all") params.category_id = filterCategory;
      if (filterDateStart) params.date_start = filterDateStart;
      if (filterDateEnd) params.date_end = filterDateEnd;
      const res = await exportAdminListings(params);
      const rows = res.list || [];
      if (rows.length === 0) {
        showToast("当前筛选条件下没有可导出的数据");
        return;
      }
      downloadListingsCsv(rows);
      showToast(`已导出 ${rows.length} 条信息`);
    } catch (e) {
      console.error("批量导出失败:", e);
      showToast("导出失败，请稍后重试");
    } finally {
      setExporting(false);
    }
  };

  // 行内快速编辑：保存（由 EditableCell 内部触发）
  const saveInlineEdit = useCallback(async (id: string, field: string, newValue: string) => {
    const trimmed = newValue.trim();
    const current = listings.find((l) => l.id === id);
    if (current && (current as any)[field] === trimmed) {
      setEditingCell(null);
      return;
    }
    if (field === 'title' && !trimmed) {
      showToast('标题不能为空');
      return;
    }
    setEditingSaveLoading(true);
    try {
      await updateAdminListing(id, { [field]: trimmed });
      setListings((prev) =>
        prev.map((l) => (l.id === id ? { ...l, [field]: trimmed } : l))
      );
      setEditingCell(null);
      showToast('修改已保存');
    } catch {
      showToast('保存失败');
    } finally {
      setEditingSaveLoading(false);
    }
  }, [listings, showToast]);

  // 行内快速编辑：取消
  const cancelInlineEdit = useCallback(() => {
    setEditingCell(null);
  }, []);

  /** 可编辑单元格 — 内部独立管理输入值，避免输入法闪烁 */
  function EditableCell({
    listingId,
    field,
    value,
    className = "",
    placeholder = "",
    emptyText,
    compact,
  }: {
    listingId: string;
    field: string;
    value: string;
    className?: string;
    placeholder?: string;
    emptyText?: string;
    compact?: boolean;
  }) {
    const isEditing = editingCell?.id === listingId && editingCell?.field === field;
    // 本地输入值，不与父级 editingCell 联动，避免每次按键重渲染
    const [localValue, setLocalValue] = useState(value);
    const composingRef = useRef(false);

    // 进入编辑态时同步初始值
    useEffect(() => {
      if (isEditing) {
        setLocalValue(value);
      }
    }, [isEditing, value]);

    if (isEditing) {
      return (
        <input
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onCompositionStart={() => { composingRef.current = true; }}
          onCompositionEnd={(e: any) => {
            composingRef.current = false;
            setLocalValue(e.target.value);
          }}
          onBlur={() => {
            if (!composingRef.current) {
              saveInlineEdit(listingId, field, localValue);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !composingRef.current) {
              saveInlineEdit(listingId, field, localValue);
            }
            if (e.key === 'Escape') cancelInlineEdit();
          }}
          autoFocus
          className={`px-1 py-0 leading-none border border-orange-400 rounded bg-orange-50/30 outline-none box-border ${compact ? 'w-[80px]' : ''} ${className}`}
          placeholder={placeholder}
          disabled={editingSaveLoading}
          style={compact ? { height: '1.5em' } : { width: '100%' }}
        />
      );
    }
    return (
      <span
        onClick={() => setEditingCell({ id: listingId, field })}
        className={`cursor-pointer hover:bg-orange-50 rounded px-1 py-0 -mx-1 transition-colors border border-transparent hover:border-orange-300 box-border ${className}`}
        title={value ? `点击编辑${field === 'title' ? '标题' : field === 'contact_name' ? '联系人' : '电话'}` : `点击添加${field === 'title' ? '标题' : field === 'contact_name' ? '联系人' : '电话'}`}
      >
        {value || <span className="text-gray-300 italic">{emptyText || '点击编辑'}</span>}
      </span>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Toast */}
        {toast && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-gray-800 text-white text-sm px-5 py-2.5 rounded-xl">
            <i className="ri-checkbox-circle-line mr-2 text-green-400" />
            {toast}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">信息管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理所有用户发布的服务信息</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAiModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-magic-line" />
              AI创作
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-cloud-line" />
              批量导入
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-download-cloud-line" />
              )}
              {exporting ? "导出中..." : "批量导出"}
            </button>
            <button
              onClick={() => setShowBatchModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-tools-line" />
              批量工具
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          {/* Row 1: search + status */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm" />
              </div>
              <input
                type="text"
                placeholder="搜索标题、作者、分类..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { key: "all", label: "全部" },
                { key: "pending", label: "待审核" },
                { key: "active", label: "已发布" },
                { key: "offline", label: "已下架" },
                { key: "rejected", label: "已驳回" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => { setFilterStatus(f.key); setCurrentPage(1); }}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    filterStatus === f.key
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: category + date filter */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Category select */}
            <div className="relative">
              <select
                value={filterCategory}
                onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
                className={`pl-3 pr-8 py-2 text-sm border rounded-lg focus:outline-none focus:border-orange-400 appearance-none cursor-pointer ${
                  filterCategory !== "all"
                    ? "border-orange-400 bg-orange-50 text-orange-600"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                <option value="all">全部分类</option>
                {categoryList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-arrow-down-s-line text-gray-400 text-sm" />
              </div>
            </div>

            {/* Date filter toggle */}
            <button
              onClick={() => setShowDateFilter((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                hasDateFilter
                  ? "border-orange-400 bg-orange-50 text-orange-600"
                  : "border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <i className="ri-calendar-line text-sm" />
              {hasDateFilter ? `${filterDateStart || "不限"} ~ ${filterDateEnd || "不限"}` : "发布时间"}
              {showDateFilter ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
            </button>

            {/* Clear filters */}
            {(filterCategory !== "all" || hasDateFilter) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line" />
                清除筛选
              </button>
            )}

            <span className="text-xs text-gray-400 ml-auto">
              共 <strong className="text-gray-600">{total}</strong> 条结果
            </span>
          </div>

          {/* Date range panel */}
          {showDateFilter && (
            <div className="flex items-center gap-3 pt-1 pb-0.5 flex-wrap">
              <span className="text-xs text-gray-500 whitespace-nowrap">发布时间范围：</span>
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => { setFilterDateStart(e.target.value); setCurrentPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
              <span className="text-gray-400 text-sm">至</span>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => { setFilterDateEnd(e.target.value); setCurrentPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
              {hasDateFilter && (
                <button
                  onClick={() => { setFilterDateStart(""); setFilterDateEnd(""); setCurrentPage(1); }}
                  className="text-xs text-orange-500 hover:text-orange-700 cursor-pointer whitespace-nowrap"
                >
                  清除日期
                </button>
              )}
            </div>
          )}
        </div>

        {/* Selected bar */}
        {selectedIds.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm text-orange-700">
              已选中 <strong>{selectedIds.length}</strong> 条信息
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setConfirmModal("batchOffline")}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-down-circle-line" />
                批量下架
              </button>
              <button
                onClick={() => setConfirmModal("batchDelete")}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-red-200 text-red-500 hover:bg-red-50 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-delete-bin-line" />
                批量删除
              </button>
              <button
                onClick={() => setSelectedIds([])}
                className="text-xs text-orange-500 hover:text-orange-700 cursor-pointer whitespace-nowrap ml-1"
              >
                取消全选
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-400 border-b border-gray-100">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 accent-orange-500 cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-3 font-medium">标题</th>
                  <th className="text-left px-4 py-3 font-medium">分类</th>
                  <th className="text-left px-4 py-3 font-medium">发布者</th>
                  <th className="text-left px-4 py-3 font-medium">发布时间</th>
                  <th className="text-left px-4 py-3 font-medium">浏览</th>
                  <th className="text-left px-4 py-3 font-medium">发布状态</th>
                  <th className="text-left px-4 py-3 font-medium">审核状态</th>
                  <th className="text-left px-4 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-400">
                      <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                        <i className="ri-loader-4-line text-2xl text-gray-300 animate-spin" />
                      </div>
                      加载中...
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-400">
                      <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                        <i className="ri-inbox-line text-2xl text-gray-300" />
                      </div>
                      暂无符合条件的信息
                    </td>
                  </tr>
                ) : (
                  paginated.map((item) => {
                    const auditStatus = item.audit_status || item.status;
                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-gray-50 transition-colors ${
                          selectedIds.includes(item.id) ? "bg-orange-50/50" : ""
                        }`}
                      >
                        <td className="px-4 py-3.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={() => toggleSelect(item.id)}
                            className="w-3.5 h-3.5 accent-orange-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <EditableCell
                              listingId={item.id}
                              field="title"
                              value={item.title}
                              className="text-sm font-medium text-gray-800 max-w-[200px] truncate block"
                              placeholder="请输入标题"
                            />
                            {item.images && item.images.length > 0 ? (
                              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-blue-50 text-blue-600 whitespace-nowrap shrink-0">
                                {item.images.length}图
                              </span>
                            ) : (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 whitespace-nowrap shrink-0">
                                无图
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {item.category_name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="space-y-0.5">
                            <EditableCell
                              listingId={item.id}
                              field="contact_name"
                              value={item.contact_name || item.user_nickname || ''}
                              className="text-sm text-gray-700 block"
                              placeholder="联系人"
                              emptyText="未设置"
                              compact
                            />
                            <EditableCell
                              listingId={item.id}
                              field="contact_phone"
                              value={item.contact_phone || item.user_phone || ''}
                              className="text-xs text-gray-400 block"
                              placeholder="电话号码"
                              emptyText="未设置"
                              compact
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">{item.view_count || 0}</td>
                        <td className="px-4 py-3.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${statusMap[item.status]?.className || 'bg-gray-100 text-gray-500'}`}>
                            {statusMap[item.status]?.label || item.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-col gap-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap w-fit ${auditStatusMap[auditStatus]?.className || 'bg-gray-100 text-gray-500'}`}>
                              {auditStatusMap[auditStatus]?.label || auditStatus}
                            </span>
                            {auditStatus === "rejected" && item.reject_reason && (
                              <span className="text-xs text-gray-400 max-w-[120px] truncate" title={item.reject_reason}>
                                {item.reject_reason}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {(auditStatus === "pending" || item.status === "pending") && (
                              <>
                                <button
                                  onClick={() => handleApprove(item.id)}
                                  className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-50 text-green-600 hover:bg-green-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                                >
                                  <i className="ri-check-line" />
                                  通过
                                </button>
                                <button
                                  onClick={() => setRejectTarget({ id: item.id, title: item.title })}
                                  className="flex items-center gap-1 px-2.5 py-1 text-xs bg-red-50 text-red-500 hover:bg-red-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                                >
                                  <i className="ri-close-line" />
                                  驳回
                                </button>
                                <span className="text-gray-200">|</span>
                              </>
                            )}
                            <button
                              onClick={() => window.open(`/listing/${item.id}`, '_blank')}
                              className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
                              title="在新标签页查看帖子"
                            >
                              <i className="ri-eye-line" />
                            </button>
                            <span className="text-gray-200">|</span>
                            <button
                              onClick={() => navigate(`/admin/edit-listing/${item.id}`)}
                              className="text-xs text-gray-500 hover:text-orange-500 cursor-pointer whitespace-nowrap"
                            >
                              编辑
                            </button>
                            <span className="text-gray-200">|</span>
                            <button
                              onClick={() => handleToggleStatus(item.id)}
                              className="text-xs text-gray-500 hover:text-orange-500 cursor-pointer whitespace-nowrap"
                            >
                              {item.status === "active" || item.status === "published" ? "下架" : "上架"}
                            </button>
                            <span className="text-gray-200">|</span>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="text-xs text-gray-500 hover:text-red-500 cursor-pointer"
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 flex-wrap gap-3">
            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">每页显示</span>
              <div className="relative">
                <button
                  onClick={() => setShowPageSizeMenu(!showPageSizeMenu)}
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded-lg hover:border-orange-400 cursor-pointer"
                >
                  {pageSize}
                  <i className="ri-arrow-down-s-line text-gray-400" />
                </button>
                {showPageSizeMenu && (
                  <div className="absolute bottom-full mb-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[70px]">
                    {[10, 20, 50, 100].map((size) => (
                      <button
                        key={size}
                        onClick={() => { setPageSize(size); setShowPageSizeMenu(false); }}
                        className={`block w-full px-3 py-1.5 text-xs text-left hover:bg-orange-50 cursor-pointer ${
                          pageSize === size ? "text-orange-500 font-medium" : "text-gray-600"
                        }`}
                      >
                        {size} 条
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-500">条</span>
              <span className="text-xs text-gray-400 ml-2">
                共 {total} 条记录
              </span>
            </div>

            {/* Page numbers */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadData(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => loadData(p)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm cursor-pointer ${
                    currentPage === p
                      ? "bg-orange-500 text-white"
                      : "border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => loadData(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {rejectTarget && (
        <RejectModal
          listingTitle={rejectTarget.title}
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {/* Batch Delete Confirm */}
      {confirmModal === "batchDelete" && (
        <ConfirmModal
          title={`确认删除 ${selectedIds.length} 条信息？`}
          desc="删除后数据将无法恢复，请谨慎操作。"
          confirmText="确认删除"
          confirmClass="bg-red-500 hover:bg-red-600"
          onConfirm={handleBatchDelete}
          onClose={() => setConfirmModal(null)}
        />
      )}

      {/* Batch Offline Confirm */}
      {confirmModal === "batchOffline" && (
        <ConfirmModal
          title={`确认下架 ${selectedIds.length} 条信息？`}
          desc="下架后信息将不再对外展示，可随时重新上架。"
          confirmText="确认下架"
          confirmClass="bg-orange-500 hover:bg-orange-600"
          onConfirm={handleBatchOffline}
          onClose={() => setConfirmModal(null)}
        />
      )}

      {/* Batch Tools Modal */}
      {showBatchModal && (
        <BatchToolsModal
          totalCount={listings.length}
          selectedIds={selectedIds}
          onApplyTime={handleApplyTime}
          onApplyPublisher={handleApplyPublisher}
          onClose={() => setShowBatchModal(false)}
        />
      )}

      {/* Batch Import Modal */}
      {showImportModal && (
        <BatchImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={(count) => {
            setShowImportModal(false);
            showToast(`成功导入 ${count} 条信息`);
            loadData(currentPage);
          }}
        />
      )}

      {/* AI Image Generation Modal */}
      {showAiModal && (
        <AiImageModal
          listings={listings}
          onClose={() => setShowAiModal(false)}
          onSuccess={(count) => {
            showToast(`AI图片生成完成，成功 ${count} 条`);
            loadData(currentPage);
          }}
        />
      )}
    </AdminLayout>
  );
}
